import { DisputeFormProps } from '@anm/api/modules/stocks';
import { useCallback, useEffect, useState, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUpgradeLinkProps } from '@anm/helpers/upgradeLink';

import vtConfigs from '../../../config';
import { stockActions, stockSelectors } from '../../../store/modules/stocks';
import { userActions, userSelectors } from '../../../store/modules/user';

const { isProd } = vtConfigs();

export const useDeleteHandler = (channel: string) => {
  const dispatch = useDispatch();

  const handleDeleteChanel = useCallback(() => {
    dispatch(userActions.deleteYoutubeChannel(channel));
  }, [channel]);

  return handleDeleteChanel;
};

const useClearState = () => {
  const dispatch = useDispatch();

  useEffect(
    () => () => {
      dispatch(stockActions.clearYoutubeChannelsState());
      dispatch(stockActions.clearSubmitYoutubeDisputeCopyrightClaimState());
    },
    []
  );
};

export const useYoutubeListHandlers = () => {
  useClearState();
  const dispatch = useDispatch();
  const [inputText, setInputText] = useState('');

  const handleAddChannel = useCallback(() => {
    dispatch(userActions.addYoutubeChannel(inputText));
    setInputText('');
  }, [inputText]);

  const handleChange = useCallback(({ target }: ChangeEvent<HTMLInputElement>) => {
    setInputText(target.value);
  }, []);

  return [inputText, handleChange, handleAddChannel] as const;
};

export const useAddChannelIsButtonDisable = (inputText: string) => {
  const isUserFree = useSelector(userSelectors.selectIsUserFree);
  const isCreator = useSelector(userSelectors.selectIsUserCreator);
  const isBusiness = useSelector(userSelectors.selectIsBusinessUser);

  const channels = useSelector(userSelectors.selectUserYoutubeChannels);

  const channelsCount = channels?.length || 0;

  const canShowTooltip = isUserFree || (isCreator && channelsCount >= 1) || (isBusiness && channelsCount >= 20);

  const isButtonDisabled = !inputText || channels?.includes(inputText) || canShowTooltip;

  const renderTooltip = useCallback(
    () => (
      <span>
        To add more channels to your account, please <a {...getUpgradeLinkProps()}>upgrade</a>
      </span>
    ),
    []
  );

  return [isButtonDisabled, canShowTooltip, renderTooltip] as const;
};

export const useChannelList = () => {
  const dispatch = useDispatch();
  const isUserPending = useSelector(userSelectors.selectUserPending);
  const channels = useSelector(userSelectors.selectUserYoutubeChannels);

  const handleDeleteChanel = useCallback((channel: string) => {
    dispatch(userActions.deleteYoutubeChannel(channel));
  }, []);

  return [isUserPending, channels, handleDeleteChanel] as const;
};

export const useCopyrightClaimHandlers = () => {
  const dispatch = useDispatch();

  const channels = useSelector(userSelectors.selectUserYoutubeChannels);
  const isPendingSubmit = useSelector(stockSelectors.selectYoutubeDisputeClaimIsPending);

  const hasChannels = !!channels.length;

  const handleSubmit = useCallback((formValues: DisputeFormProps) => {
    dispatch(
      stockActions.submitYoutubeDisputeCopyrightClaim({
        ...formValues,
        is_test: isProd ? 0 : 1
      })
    );
  }, []);

  return [hasChannels, isPendingSubmit, handleSubmit] as const;
};
